import React from 'react';
import styled from '@emotion/styled';
import { colors } from 'src/theme';

interface RadioOptionWithDescriptionProps {
  value: string;
  title: string;
  description: string;
  selected: boolean;
  onChange: (value: string) => void;
  isPopular?: boolean;
  disabled?: boolean;
}

const Container = styled.div({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  width: '100%',
  cursor: 'pointer'
});

const RadioCircle = styled.div<{ selected: boolean; disabled?: boolean }>(({ selected, disabled }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '20px',
  border: `2px solid ${selected ? colors.primary1 : colors.border4}`,
  backgroundColor: selected ? colors.primary1 : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease',

  '&::after': selected ? {
    content: '""',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: colors.white
  } : {}
}));

const ContentWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: '1 0 0',
  minWidth: '1px',
  minHeight: '1px'
});

const TitleRow = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%'
});

const Title = styled.p({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  color: colors.text2,
  margin: 0
});

const PopularBadge = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  backgroundColor: colors.success2,
  borderRadius: '4px'
});

const PopularText = styled.span({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: '16px',
  color: colors.text1,
  textAlign: 'center'
});

const Description = styled.p({
  fontFamily: "'Red Hat Text', sans-serif",
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  color: colors.text3,
  margin: 0,
  width: '257px',

  '@media (max-width: 768px)': {
    width: '100%'
  }
});

export const RadioOptionWithDescription: React.FC<RadioOptionWithDescriptionProps> = ({
  value,
  title,
  description,
  selected,
  onChange,
  isPopular = false,
  disabled = false
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  return (
    <Container onClick={handleClick}>
      <RadioCircle selected={selected} disabled={disabled} />
      <ContentWrapper>
        <TitleRow>
          <Title>{title}</Title>
          {isPopular && (
            <PopularBadge>
              <PopularText>POPULARNO</PopularText>
            </PopularBadge>
          )}
        </TitleRow>
        <Description>{description}</Description>
      </ContentWrapper>
    </Container>
  );
};
